import { IdeaEvidence } from '@models/Sessions/Idea/evidence';
import isHtml from 'is-html';
import { uniq } from 'lodash';
import MarkdownIt from 'markdown-it';
import { schema } from 'prosemirror-markdown';
import { DOMSerializer } from 'prosemirror-model';
import TurndownService from 'turndown';

import { docFromJsonString } from '../components/misc/prosemirror/util';
import { diffHtml } from './diff-html';

TurndownService.prototype.escape = (str: string) => str;

const turndown = new TurndownService({
  headingStyle: 'atx',
})
  .addRule('insertion', {
    filter: ['ins'],
    replacement: content => content,
  })
  .keep(['strong'])
  .remove(['del']);

const markdown = MarkdownIt({ breaks: true });

export const convertToHtml = (content: string) => {
  return (isHtml(content) ? content : markdown.render(content)).replace(
    /"/gm,
    '&quot;',
  );
};

export const stripMarkTags = (html: string) => {
  return html.replace(/<\/?mark[^>]*>/gi, '');
};

export const getHtmlDiff = (oldContent: string, newContent: string) => {
  const oldHtml = convertToHtml(oldContent);
  const newHtml = convertToHtml(newContent);
  const cleanOldHtml = stripMarkTags(oldHtml);
  const cleanNewHtml = stripMarkTags(newHtml);

  return diffHtml(cleanOldHtml, cleanNewHtml);
};

export const exposeLeadingHeading = (oldHtml: string, newHtml: string) => {
  oldHtml = convertToHtml(oldHtml);
  newHtml = convertToHtml(newHtml);

  if (!newHtml.startsWith('<h1>') && oldHtml.startsWith('<h1>')) {
    const leadingHeading = oldHtml.match(/^<h1>([^<]*)<\/h1>/)[1];
    return `<h1>${leadingHeading}</h1>${newHtml}`;
  }

  return newHtml;
};

export const getUsedEvidence = (content: string) =>
  uniq(content.match(/(\[|<ins>)(\d+)\]/gm)).map(v =>
    parseInt(v.replace(/\[|<ins>|\]/, '')),
  );

export const convertToMarkdown = (content: string) =>
  isHtml(content) ? turndown.turndown(content) : content;

export const hasContentChanges = (content: string) =>
  content.match(/(<ins>|<del>)/gm);

export const cleanupArtifacts = (content: string) =>
  content.replace(/^Podcast\sscript:\s+/, '');

export const convertToTextFormat = (
  content: string,
  evidence: IdeaEvidence[] = [],
  hideCitations = true,
) => {
  const usedEvidence = getUsedEvidence(content);
  let text = convertToHtml(content)
    .replace(/<\/?(h\d|p)>/g, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&quot;/gm, '"');
  if (hideCitations) {
    text = text.replace(/\s*\[\d+\]/gm, '');
  }
  if (!hideCitations && usedEvidence.length) {
    text = `${text}\n\nReferences:\n\n${generateEvidenceList(evidence.filter(e => usedEvidence.includes(e.citation)))}`;
  }
  return text;
};

export const convertToMarkdownFormat = (
  content: string,
  evidence: IdeaEvidence[] = [],
  hideCitations = true,
) => {
  const usedEvidence = getUsedEvidence(content);
  let text = convertToMarkdown(content);
  if (hideCitations) {
    text = text.replace(/\s*\[\d+\]/gm, '');
  }
  if (!hideCitations && usedEvidence.length) {
    text = `${text}\n\nReferences:\n\n${generateEvidenceList(evidence.filter(e => usedEvidence.includes(e.citation)))}`;
  }
  return text;
};

export const convertToHtmlFormat = (
  content: string,
  evidence: IdeaEvidence[] = [],
  hideCitations = true,
) => {
  const usedEvidence = getUsedEvidence(content);
  let text = convertToHtml(content);
  if (hideCitations) {
    text = text.replace(/\s*\[\d+\]/gm, '');
  }
  if (!hideCitations && usedEvidence.length) {
    text = `${text}\n\n<p>References:\n\n${generateEvidenceList(evidence.filter(e => usedEvidence.includes(e.citation)))}</p>`;
  }
  return text;
};

export const generateEvidenceList = (evidence: IdeaEvidence[]) =>
  evidence
    .map(e => `Citation: ${e.citation}\nContent: ${e.content}\nURL:${e.url}`)
    .join('\n\n----------------------------------------\n\n');

export const parseProsemirrorContet = (content: string) => {
  if (!content) {
    return null;
  }

  let result = null;

  try {
    const fragment = DOMSerializer.fromSchema(schema).serializeFragment(
      docFromJsonString(schema, content).content,
    );
    const div = document.createElement('div');
    div.appendChild(fragment);
    document.documentElement.appendChild(div);
    result = div.innerHTML;
    div.remove();
  } catch (e) {
    console.error(e);
    result = null;
  }

  return result;
};
